import { ReactElement } from 'react';

import { ReactComponent as BackgroundCircles } from '@backrs_ui/common-ui/src/assets/images/background-circles.svg';

import { Box, Dialog, DialogProps, SxProps, Theme } from '@mui/material';

import { AppIcon, AppIcons } from '../designSystem/icons/AppIcon';
import { FeaturedIcon, FeaturedIconProps } from '../designSystem/icons/FeaturedIcon';

export type InfoModalProps = Readonly<{
  open: boolean;
  onClose?: () => void;
  width?: DialogProps['maxWidth'];
  /** if you do not pass in headerContent, the FeaturedIcon will not be rendered, and the close icon will be absolutely positioned in the top right corner */
  headerContent?: ReactElement;
  headerInLineWithIcon?: boolean;
  bodyContent: ReactElement;
  dialogSx?: SxProps<Theme>;
  icon?: AppIcons;
  featuredIcon?: FeaturedIconProps;
  headerVisual?: ReactElement;
  hideCircleImage?: boolean;
  showBottomBorder?: boolean;
  bodyContainerSx?: SxProps<Theme>;
  /** Visual variant: 'light' (default) or 'dark' for dark header background with white text and styled close button */
  variant?: 'light' | 'dark';
  /** Bound the modal to the viewport with a pinned header; bodyContent manages its own scroll. */
  fitViewport?: boolean;
}>;

export const InfoView = ({
  onClose,
  headerContent,
  headerInLineWithIcon = false,
  bodyContent,
  icon = 'InfoCircle',
  featuredIcon = {
    iconName: icon,
    size: 'lg',
    color: 'Colors/Foreground/fg-secondary',
  },
  hideCircleImage = false,
  headerVisual,
  showBottomBorder = false,
  bodyContainerSx,
  variant = 'light',
  fitViewport = false,
}: Omit<InfoModalProps, 'open' | 'width' | 'dialogSx'>) => {
  const isDark = variant === 'dark';

  return (
    <Box sx={containerSx(!!bodyContainerSx, isDark, fitViewport)}>
      <Box sx={headerSx(!!headerContent, showBottomBorder, !!bodyContainerSx, isDark, fitViewport)}>
        {!!headerContent && !hideCircleImage && !isDark && (
          <Box sx={backgroundCirclesSx}>
            <BackgroundCircles />
          </Box>
        )}

        <Box sx={iconsSx(!!headerContent)}>
          {!!headerContent && !isDark && (
            <Box sx={featuredIconAndOptionalHeaderContentSx}>
              {headerVisual ?? <FeaturedIcon {...featuredIcon} />}

              {headerInLineWithIcon && headerContent}
            </Box>
          )}
          {!!headerContent && isDark && headerInLineWithIcon && headerContent}
          {onClose && (
            <Box id="info-modal-close-button" sx={closeIconSx(!!headerContent)} onClick={onClose}>
              <AppIcon
                iconName="XClose"
                diameter="20px"
                color={isDark ? 'Colors/Base/White' : 'Colors/Foreground/fg-quaternary'}
              />
            </Box>
          )}
        </Box>

        {!headerInLineWithIcon && headerContent}
      </Box>

      <Box
        sx={
          fitViewport
            ? ([fitBodySx, bodyContainerSx].filter(Boolean) as SxProps<Theme>)
            : bodyContainerSx
        }
      >
        {bodyContent}
      </Box>
    </Box>
  );
};

export const InfoModal = ({
  open,
  onClose,
  width = 'md',
  headerContent,
  headerInLineWithIcon = false,
  bodyContent,
  dialogSx,
  icon,
  featuredIcon,
  headerVisual,
  hideCircleImage = false,
  showBottomBorder = false,
  bodyContainerSx,
  variant = 'light',
  fitViewport = false,
}: InfoModalProps) => {
  return (
    <Dialog
      className="info-modal-dialog"
      open={open}
      onClose={onClose}
      sx={[dialogStyle, fitViewport && fitViewportDialogStyle, dialogSx].filter(Boolean) as SxProps}
      maxWidth={width}
      fullWidth
    >
      <InfoView
        onClose={onClose}
        headerContent={headerContent}
        headerInLineWithIcon={headerInLineWithIcon}
        bodyContent={bodyContent}
        hideCircleImage={hideCircleImage}
        icon={icon}
        featuredIcon={featuredIcon}
        headerVisual={headerVisual}
        showBottomBorder={showBottomBorder}
        bodyContainerSx={bodyContainerSx}
        variant={variant}
        fitViewport={fitViewport}
      />
    </Dialog>
  );
};

const containerSx =
  (hasCustomBodySx: boolean, isDark: boolean, fitViewport: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',

    ...(fitViewport ? { flex: 1, minHeight: 0 } : { overflowY: 'auto' }),

    ...(!hasCustomBodySx && {
      p: backrs.spacing['spacing-3xl'],
    }),

    ...(isDark && {
      backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    }),
  });

const fitBodySx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const fitViewportDialogStyle: SxProps<Theme> = {
  '& .MuiDialog-paper': {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
};

const headerSx =
  (
    hasHeaderContent: boolean,
    showBottomBorder: boolean,
    hasCustomBodySx: boolean,
    isDark: boolean,
    fitViewport: boolean,
  ): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    width: '100%',

    ...(fitViewport && { flexShrink: 0 }),

    ...(hasCustomBodySx && {
      px: backrs.spacing['spacing-3xl'],
      pt: backrs.spacing['spacing-3xl'],
    }),

    ...(showBottomBorder && {
      marginX: -backrs.spacing['spacing-3xl'],
      borderBottom: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
    }),
    ...(hasHeaderContent && {
      mb: isDark ? 0 : backrs.spacing['spacing-2xl'],
    }),

    ...(isDark && {
      backgroundColor: backrs.colors['Colors/Base/Black'],
      px: backrs.spacing['spacing-3xl'],
      pt: backrs.spacing['spacing-2xl'],
      pb: backrs.spacing['spacing-xl'],
    }),
  });

const iconsSx =
  (hasHeaderContent: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    zIndex: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',

    ...(hasHeaderContent && {
      mb: backrs.spacing['spacing-xl'],
      justifyContent: 'space-between',
    }),
  });

const closeIconSx =
  (hasHeaderContent: boolean): SxProps<Theme> =>
  () => ({
    cursor: 'pointer',
    justifySelf: 'flex-end',

    ...(!hasHeaderContent && {
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: 1000,
    }),
  });

const backgroundCirclesSx: SxProps<Theme> = {
  position: 'absolute',
  top: -(336 * 0.425),
  left: -(336 * 0.425),
  zIndex: 0,
};

const dialogStyle: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiDialog-paper': {
    borderRadius: backrs.radius['radius-xl'],
    boxShadow: backrs.shadows['shadow-xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
});

const featuredIconAndOptionalHeaderContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-2xl'],
});
